<template>
  <main-dialog
    :visible="show"
    @hidden="$emit('close')"
    title="PDF Form Fields"
    size="lg"
    hide-footer
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Loading form fields...</p>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      <i class="fa fa-exclamation-triangle"></i>
      {{ error }}
    </div>
    
    <div v-else-if="formData">
      <div class="form-fields-header mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong>File:</strong> {{ formData.path }}
          </div>
          <div>
            <b-badge variant="primary">{{ formData.field_count }} fields</b-badge>
          </div>
        </div>
      </div>
      
      <div v-if="formData.form_fields && formData.form_fields.length > 0">
        <b-table
          :items="formData.form_fields"
          :fields="tableFields"
          striped
          hover
          responsive
          small
          class="form-fields-table"
        >
          <template #cell(name)="data">
            <code class="field-name">{{ data.value }}</code>
          </template>
          
          <template #cell(type)="data">
            <b-badge :variant="getTypeVariant(data.value)">
              {{ data.value }}
            </b-badge>
          </template>
          
          <template #cell(pages)="data">
            <span class="text-muted">{{ data.value.join(', ') }}</span>
          </template>
          
          <template #cell(current_value)="data">
            <span v-if="data.value" class="text-success">{{ data.value }}</span>
            <span v-else class="text-muted">-</span>
          </template>
          
          <template #cell(locked)="data">
            <i :class="data.value ? 'fa fa-lock text-warning' : 'fa fa-unlock text-muted'"></i>
          </template>
        </b-table>
      </div>
      
      <div v-else class="alert alert-info">
        <i class="fa fa-info-circle"></i>
        No form fields found in this PDF.
      </div>
    </div>
  </main-dialog>
</template>

<script>
export default {
  name: 'PdfFormFieldsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    file: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      formData: null,
      tableFields: [
        { key: 'name', label: 'Field Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'pages', label: 'Pages', sortable: true },
        { key: 'current_value', label: 'Current Value', sortable: false },
        { key: 'locked', label: 'Locked', sortable: true, class: 'text-center' }
      ]
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadFormFields();
      } else {
        // Reset data when modal is closed
        this.formData = null;
        this.error = null;
      }
    }
  },
  methods: {
    async loadFormFields() {
      this.loading = true;
      this.error = null;
      this.formData = null;
      
      try {
        const response = await this.$mediaHttp.post(
          '/v3/dms/media/pdf/fields',
          { path: this.file.key },
          { params: { detailed: true } }
        );
        
        this.formData = response.data;
      } catch (error) {
        console.error('Failed to load form fields:', error);
        
        if (error.response && error.response.status === 400) {
          this.$bvToast.toast('No form fields detected in this PDF', {
            title: 'Info',
            variant: 'info'
          });
          this.$emit('close');
        } else {
          this.error = (error.response && error.response.data && error.response.data.message) || 'Failed to load PDF form fields';
        }
      } finally {
        this.loading = false;
      }
    },
    
    getTypeVariant(type) {
      const variants = {
        'Textfield': 'info',
        'Checkbox': 'success',
        'Radio': 'warning',
        'Dropdown': 'primary',
        'Signature': 'danger'
      };
      return variants[type] || 'secondary';
    }
  }
};
</script>

<style scoped lang="scss">
.form-fields-header {
  padding: 12px;
  background: var(--form-bg);
  border-radius: 6px;
  color: var(--text-primary);
}

.field-name {
  color: var(--primary-start);
  font-size: 13px;
  background: rgba(108, 99, 255, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
}

.form-fields-table {
  font-size: 13px;
}

.form-fields-table >>> tbody tr:hover {
  background-color: rgba(108, 99, 255, 0.05);
}
</style>
